// =============================================
// BulkAutoGenerateContext — App-level state dla bulk auto-generate (KOREKTA 6.4)
// =============================================
// useAutoGenerateBulk hook trzyma state w komponencie — nawigacja Lista map ->
// Auto-generuj page kasuje running session. Provider liftuje to do App level,
// dzieki czemu UI overlay przezywa nawigacje.
//
// Wrap z `<BulkAutoGenerateProvider>` w `src/App.tsx` (poziom rootowy).
// Komponenty uzywaja `useBulkAutoGenerateContext()` (NIE useAutoGenerateBulk
// bezposrednio, bo wtedy state nie persistuje).
// =============================================

import { createContext, useContext, type ReactNode } from "react";
import {
  useAutoGenerateBulk,
  type AutoGenerateBulkProgress,
} from "@/hooks/useAutoGenerateBulk";
import type {
  BulkAutoGenerateOptions,
  BulkSummary,
} from "@/services/autoGenerate/autoGenerateBulkRunner";

interface BulkAutoGenerateContextValue {
  running: boolean;
  progress: AutoGenerateBulkProgress;
  summary: BulkSummary | null;
  start: (mapIds: string[], options: BulkAutoGenerateOptions) => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => void;
}

const BulkAutoGenerateContext =
  createContext<BulkAutoGenerateContextValue | null>(null);

export function BulkAutoGenerateProvider({ children }: { children: ReactNode }) {
  // KLUCZOWE: hook wywolany RAZ w Provider — state persistuje cross-nav.
  const bulk = useAutoGenerateBulk();
  return (
    <BulkAutoGenerateContext.Provider value={bulk}>
      {children}
    </BulkAutoGenerateContext.Provider>
  );
}

export function useBulkAutoGenerateContext(): BulkAutoGenerateContextValue {
  const ctx = useContext(BulkAutoGenerateContext);
  if (!ctx) {
    throw new Error(
      "useBulkAutoGenerateContext must be used wewnatrz BulkAutoGenerateProvider",
    );
  }
  return ctx;
}
